import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { UserCertificateRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import {
  IApiResponseGroupCertificates,
  ICertificate,
  ICertificateGroup,
  ICertificateGroupData,
  ICertificateLog,
} from '../models/certificate.model';
import { CertificateApiService } from '../service/certificate-api.service';
import {
  ClearCertificatesByMenu,
  ClearCurrentCertificate,
  CreateCertificate,
  DeleteCertificate,
  LoadCertificateGroups,
  LoadCertificateLog,
  LoadCertificatesByMenu,
  LoadCertificatesForIO,
  LoadSpecificCertificate,
  RenewCertificate,
  RestoreCertificate,
  UpdateCertificate,
} from './certificate.actions';

export interface CertificateStateModel {
  certificatesStructuresLoaded: boolean;
  certificates: ICertificate[];
  certificatesTotalPages: number;
  groupedCertificates: ICertificateGroupData[];
  certificatesGrouped: ICertificateGroup[];
  certificatesGroupedTotalPages: number;
  currentCertificate: ICertificate | undefined;
  certificateLogs: ICertificateLog[];
  certificateLogsTotalPages: number;
  cetificatesForIO: IdName[];
}

const CERTIFICATE_STATE_TOKEN = new StateToken<CertificateStateModel>('certificate');

@State<CertificateStateModel>({
  name: CERTIFICATE_STATE_TOKEN,
  defaults: {
    certificatesStructuresLoaded: false,
    certificates: [],
    certificatesTotalPages: 0,
    groupedCertificates: [],
    certificatesGrouped: [],
    certificatesGroupedTotalPages: 0,
    currentCertificate: undefined,
    certificateLogs: [],
    certificateLogsTotalPages: 0,
    cetificatesForIO: [],
  },
})
@Injectable()
export class CertificateState {
  constructor(
    private service: CertificateApiService,
    private store: Store
  ) {}

  @Action(LoadCertificatesByMenu, { cancelUncompleted: true })
  loadCertificatesByMenu(
    ctx: StateContext<CertificateStateModel>,
    { filter }: LoadCertificatesByMenu
  ) {
    return this.service.getCertificatesByMenuWithFiltering(filter).pipe(
      tap((response: IApiResponseGroupCertificates) => {
        if (response.groupData) {
          ctx.setState(
            patch({
              certificates: response.data,
              certificatesTotalPages: response.totalPages,
              groupedCertificates: response.groupData,
            })
          );
        }
      })
    );
  }

  @Action(LoadCertificateGroups, { cancelUncompleted: true })
  loadCertificateGroups(
    { getState, setState }: StateContext<CertificateStateModel>,
    { filter }: LoadCertificateGroups
  ) {
    return this.service.getAllCertificatesGrouped(filter).pipe(
      tap((response: ApiResponse<ICertificateGroup[]>) => {
        const state = getState();
        setState({
          ...state,
          certificatesGrouped: response.data,
          certificatesGroupedTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadSpecificCertificate, { cancelUncompleted: true })
  loadSpecificCertificate(
    { getState, setState }: StateContext<CertificateStateModel>,
    { certificateId }: LoadSpecificCertificate
  ) {
    return this.service.getSpecificCertificate(certificateId).pipe(
      tap((response: ICertificate) => {
        const state = getState();
        setState({
          ...state,
          currentCertificate: response,
        });
      })
    );
  }

  @Action(LoadCertificateLog, { cancelUncompleted: true })
  loadCertificateLog(
    { getState, setState }: StateContext<CertificateStateModel>,
    { certificateId, filter }: LoadCertificateLog
  ) {
    return this.service.loadCertificateLog(certificateId, filter).pipe(
      tap((response: ApiResponse<ICertificateLog[]>) => {
        const state = getState();
        setState({
          ...state,
          certificateLogs: response.data,
          certificateLogsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadCertificatesForIO, { cancelUncompleted: true })
  loadCertificatesForIO({ getState, setState }: StateContext<CertificateStateModel>) {
    return this.service.loadCertificatesForIO().pipe(
      tap((response: IdName[]) => {
        const state = getState();
        setState({
          ...state,
          cetificatesForIO: response,
        });
      })
    );
  }

  @Action(CreateCertificate, { cancelUncompleted: true })
  @OnSuccess({ message: 'certificateCreatedSuccessfully' })
  createCertificate(ctx: StateContext<CertificateStateModel>, { certificate }: CreateCertificate) {
    return this.service.createCertificate(certificate).pipe(
      tap((response: { id: string }) => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? certificate.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserCertificateRoutes.Overview}/${menuId}`);
      })
    );
  }

  @Action(UpdateCertificate, { cancelUncompleted: true })
  @OnSuccess({ message: 'certificateUpdatedSuccessfully' })
  updateCertificate(ctx: StateContext<CertificateStateModel>, { certificate }: UpdateCertificate) {
    return this.service.updateCertificate(certificate).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? certificate.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserCertificateRoutes.Overview}/${menuId}`);
      })
    );
  }

  @Action(DeleteCertificate, { cancelUncompleted: true })
  @OnSuccess({ message: 'certificateDeletedSuccessfully' })
  deleteCertificate(
    ctx: StateContext<CertificateStateModel>,
    { certificateId }: DeleteCertificate
  ) {
    return this.service.deleteCertificate(certificateId).pipe(
      tap(() => {
        const menuId = this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId);
        let url = `${UserCertificateRoutes.Main}`;
        if (menuId) {
          url = `${UserCertificateRoutes.Overview}/${menuId}`;
        }
        ctx.dispatch(new ResetCurrentFilter());
        redirectAndBypassUnsavedDataCheck(url);
      })
    );
  }

  @Action(RenewCertificate, { cancelUncompleted: true })
  renewCertificate(ctx: StateContext<CertificateStateModel>, { certificate }: RenewCertificate) {
    return this.service.renewCertificate(certificate).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserCertificateRoutes.Overview}/${certificate.menuId}`
        );
      })
    );
  }

  @Action(ClearCertificatesByMenu, { cancelUncompleted: true })
  clearCertificatesByMenu(ctx: StateContext<CertificateStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      groupedCertificates: [],
      certificatesTotalPages: 0,
    });
  }

  @Action(ClearCurrentCertificate, { cancelUncompleted: true })
  clearCurrentCertificate(ctx: StateContext<CertificateStateModel>) {
    ctx.setState(patch({ currentCertificate: undefined }));
  }

  @Action(RestoreCertificate, { cancelUncompleted: true })
  @OnSuccess({ message: 'certificateRestored', url: UserCertificateRoutes.MainActive })
  restoreCertificate(ctx: StateContext<CertificateStateModel>, { id }: RestoreCertificate) {
    return this.service.restoreCertificate(id);
  }

  @Selector()
  static getCertificatesByMenu(state: CertificateStateModel) {
    return state.groupedCertificates;
  }

  @Selector()
  static getTotalPagesForCertificates(state: CertificateStateModel) {
    return state.certificatesTotalPages;
  }

  @Selector()
  static getAllCertificateGroupedPages(state: CertificateStateModel) {
    return state.certificatesGroupedTotalPages;
  }

  @Selector()
  static getAllCertificates(state: CertificateStateModel) {
    return state.certificates;
  }

  @Selector()
  static getCurrentCertificate(state: CertificateStateModel) {
    return state.currentCertificate;
  }

  @Selector()
  static getAllCertificateGrouped(state: CertificateStateModel) {
    return state.certificatesGrouped;
  }

  @Selector()
  static getCertificateById(state: CertificateStateModel) {
    return (index: any) => {
      return state.certificates[index];
    };
  }

  @Selector()
  static getCertificateLogs(state: CertificateStateModel): ICertificateLog[] {
    return state.certificateLogs;
  }

  @Selector()
  static getCertificateLogsTotalPages(state: CertificateStateModel): number {
    return state.certificateLogsTotalPages;
  }

  @Selector()
  static getCertificatesForIO(state: CertificateStateModel) {
    return state.cetificatesForIO;
  }
}
